/**
 * Manages explicit user/group grants while preserving effective Vault ownership.
 *
 * The panel distinguishes direct grants from inherited access and prevents
 * owner lockout. Backend policy remains authoritative; this component exposes
 * the consequences clearly and refreshes the rendered grant state after edits.
 */
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import {
  getSharing,
  listShareCandidates,
  removeCollaborator,
  removeOrganizationCollaborator,
  setCollaborator,
  setOrganizationCollaborator,
} from '../services/vault-api'
import { ShareCandidateGroup } from './ShareCandidateGroup'
import {
  ROLE_LABELS,
  type ShareRole,
  VaultGrantList,
} from './VaultGrantList'

export function VaultAccessManager({
  api,
  orgId,
  vaultId,
  itemId,
  readOnly,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  itemId: string
  readOnly: boolean
}) {
  const [invite, setInvite] = useState('')
  const [inviteRole, setInviteRole] = useState<ShareRole>('editor')
  const queryClient = useQueryClient()
  const sharingKey = ['vault-sharing', orgId, vaultId, itemId]
  const sharingQuery = useQuery({
    queryKey: sharingKey,
    queryFn: () => getSharing(api, orgId, vaultId, itemId),
  })
  const candidatesQuery = useQuery({
    queryKey: ['vault-share-candidates', orgId, vaultId],
    queryFn: () => listShareCandidates(api, orgId, vaultId),
  })
  const sharing = sharingQuery.data
  const canShare = sharing?.canManage === true
  const workspaceRoot = itemId === 'ROOT' && Boolean(sharing?.workspaceAccess.length)
  const selectedInviteRole = readOnly || workspaceRoot ? 'viewer' : inviteRole
  const userGrants = useMemo(
    () => sharing?.directGrants.filter(grant => !grant.orgPrincipal) ?? [],
    [sharing],
  )
  const organizationGrant = sharing?.directGrants.find(grant => grant.orgPrincipal)
  const suggestions = useMemo(() => {
    if (!sharing) {
      return []
    }
    const granted = new Set(userGrants.map(grant => grant.principalId))
    const term = invite.trim().toLowerCase()
    const available = (members: string[]) => members
      .filter(member => member !== sharing.ownerUserId && !granted.has(member))
      .filter(member => member.toLowerCase().includes(term))
    return available(candidatesQuery.data?.otherOrganizationMembers ?? [])
  }, [candidatesQuery.data, invite, sharing, userGrants])

  const mutate = async (action: () => Promise<unknown>, success: string) => {
    try {
      await action()
      await queryClient.invalidateQueries({ queryKey: sharingKey })
      toast.success(success)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? 'Sharing update failed')
    }
  }
  const inviteMember = (userId: string) =>
    mutate(
      () => setCollaborator(
        api, orgId, vaultId, itemId, userId, selectedInviteRole,
      ),
      `Shared with ${userId}`,
    ).then(() => setInvite(''))

  if (sharingQuery.isPending) {
    return <p className="text-sm text-muted-foreground">Loading access…</p>
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        {workspaceRoot
          ? 'Workspace access is inherited. Additional recipients can view the whole Vault; grant editing access when sharing an individual writable folder or file.'
          : sharing?.workspaceAccess.length
            ? 'Workspace access is inherited. Share this item with people outside the workspace or everyone at UMD.'
          : canShare
            ? 'Grant access to other UMD people or everyone at UMD.'
          : 'Only the item owner can change access. Vault administrators manage whole-Vault access.'}
      </p>

      {canShare && (
        <div className="space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={invite}
              onChange={event => setInvite(event.target.value)}
              placeholder="Search UMD people by username"
              aria-label="Grant Vault access"
            />
            {!workspaceRoot && (
              <Select
                value={selectedInviteRole}
                onValueChange={value => setInviteRole(value as ShareRole)}
              >
                <SelectTrigger className="w-full shrink-0 sm:w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {!readOnly && (
                    <SelectItem value="editor">{ROLE_LABELS.editor}</SelectItem>
                  )}
                  <SelectItem value="viewer">{ROLE_LABELS.viewer}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          {invite.trim() && (
            <div className="overflow-hidden rounded-md border animate-in fade-in slide-in-from-top-1 duration-150">
              <ShareCandidateGroup
                label="Other UMD people"
                members={suggestions}
                onSelect={member => void inviteMember(member)}
              />
              {!suggestions.length && (
                  <p className="px-3 py-2 text-sm text-muted-foreground">
                    No matching UMD people.
                  </p>
              )}
            </div>
          )}
        </div>
      )}

      <VaultGrantList
        sharing={sharing}
        userGrants={userGrants}
        organizationGrant={organizationGrant}
        canShare={canShare}
        readOnly={readOnly}
        workspaceRoot={workspaceRoot}
        onSetUserRole={(userId, role) => void mutate(
          () => setCollaborator(api, orgId, vaultId, itemId, userId, role),
          'Access updated',
        )}
        onRemoveUser={userId => void mutate(
          () => removeCollaborator(api, orgId, vaultId, itemId, userId),
          'Access removed',
        )}
        onSetOrganizationRole={role => void mutate(
          () => role === 'none'
            ? removeOrganizationCollaborator(api, orgId, vaultId, itemId)
            : setOrganizationCollaborator(api, orgId, vaultId, itemId, role),
          'Organization access updated',
        )}
      />
    </div>
  )
}
